import { subscriptionAtom } from '@/atoms/subscription.atom';
import { userAtom } from '@/atoms/user.atom';
import { SubscriptionTier } from '@/graphql/graphql';
import { FEATURE_GATES, FeatureKey, effectiveTier, isWhitelistedEmail, tierAtLeast } from '@/lib/featureGates';
import { useDialog } from '@/providers/DialogProvider';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { UpgradeDialog } from '@/components/subscription/UpgradeDialog';

export interface FeatureResult {
  allowed: boolean;
  requiredTier: SubscriptionTier;
  currentTier: SubscriptionTier;
  promptUpgrade: () => void;
}

export const useFeature = (key: FeatureKey): FeatureResult => {
  const subscription = useAtomValue(subscriptionAtom);
  const user = useAtomValue(userAtom);
  const { openDialog } = useDialog();

  const gate = FEATURE_GATES[key];
  const currentTier = effectiveTier(subscription);
  const allowed = isWhitelistedEmail(user?.email) || tierAtLeast(currentTier, gate.minTier);

  const promptUpgrade = useCallback(() => {
    openDialog(<UpgradeDialog feature={key} />);
  }, [openDialog, key]);

  return useMemo(
    () => ({ allowed, requiredTier: gate.minTier, currentTier, promptUpgrade }),
    [allowed, gate.minTier, currentTier, promptUpgrade],
  );
};

// Imperative variant for use inside event handlers / non-render contexts.
// Returns a stable function that evaluates a feature and (optionally) opens the upgrade dialog.
export const useFeatureCheck = () => {
  const subscription = useAtomValue(subscriptionAtom);
  const user = useAtomValue(userAtom);
  const { openDialog } = useDialog();
  const currentTier = effectiveTier(subscription);
  const whitelisted = isWhitelistedEmail(user?.email);

  return useCallback(
    (key: FeatureKey): FeatureResult => {
      const gate = FEATURE_GATES[key];
      const allowed = whitelisted || tierAtLeast(currentTier, gate.minTier);
      return {
        allowed,
        requiredTier: gate.minTier,
        currentTier,
        promptUpgrade: () => openDialog(<UpgradeDialog feature={key} />),
      };
    },
    [currentTier, whitelisted, openDialog],
  );
};
